import CircularProgress from "components/CircularProgress/CircularProgress";
import { CommonCardContainer } from "components/DashboardCards/cardStyles";
import ComponentCircleLoader from "components/Loaders/ComponentCircleLoader";
import RightSlidingPanel from "components/RightSlidingPanel/RightSlidingPanel";
import Status from "components/Status/Status";
import {
  GreenCircle,
  LateTag,
} from "custom_modules/FieldServices/RouteMapNew/routeMapStyles";
import { CommImageNameComp } from "custom_modules/Members/CommonComponents";
import moment from "moment-timezone";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HorizontalLineDivider,
  ToolTipContainer,
} from "styledComponents/Activity";
import { TableItem } from "styledComponents/ProjectsNew";
import { ColoredButton } from "styledComponents/buttons";
import { InnerDivTooltip, TooltipText } from "styledComponents/clients";
import {
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonStatusContainer,
  CommonText,
  ScheduleForContainer,
  ScheduleForItemContainer,
  SemiRoundedButton,
  StatusDot,
  SubtaskTableContainer,
  TableText,
} from "styledComponents/common";
import { MemberNameDiv } from "styledComponents/members";
import {
  AssigneeListTableHeaderContainer,
  AssigneeListTableRowContainer,
} from "styledComponents/table";

import avatar5 from "../../../assets/img/avatar_5.svg";
import imageIcon from "../../../assets/img/icons/Image.svg";
import calendarIcon from "../../../assets/img/icons/calender.svg";
import Chat from "../../../assets/img/icons/chat.svg";
import audioIcon from "../../../assets/img/icons/play-icon.svg";
import tick from "../../../assets/img/icons/tick-white-bold.svg";
import useAudioDurations from "../../../hooks/useAudioDurations";
import { getMapCircle } from "../../../utils/helper";
import {
  SubTaskTableHeaderContainer,
  SubTaskTableRowContainer,
} from "../CreateJob/createTaskStyles";
import EvidenceGalleryModal from "../../EvidenceGallaryModal/EvidenceGalleryModal";
import { ApproveRejectButton } from "./jobStyles";
import {
  EvidenceIcon,
  EvidenceIconSection,
  NoteSection,
} from "./taskApprovalStyle";
import LineItemSection from "custom_modules/JobComponents/LineItemSection";
import DocumentUploadSection from "custom_modules/JobComponents/DocumentUploadSection";

const ApprovalRightpanel = ({
  toggle,
  isOpen,
  isLoading,
  jobDetails,
  timezone,
  setSupervisorNoteModalOpen,
  selectedOrganization,
  updateTaskStatusRequest,
  setRejectedJobInfo,
}) => {
  const role = localStorage.getItem("selected_organization_role");
  const user_id = localStorage.getItem("user_id");
  const { t, i18n } = useTranslation();
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const listPath = isOperationType ? "/user/tasks" : "/user/jobs";
  const editPath = isOperationType ? "/user/edit-task" : "/user/edit-job";
  const detailsPageTitle = isOperationType
    ? t("task_details")
    : t("job_details");
  const backToAllItemsLabel = isOperationType
    ? t("back_to_all_tasks")
    : t("back_to_all_jobs");
  const itemLabel = isOperationType ? t("task") : t("job");
  const jobLabel = isOperationType ? t("task") : t("job");
  const itemProgressLabel = isOperationType
    ? t("task_progress")
    : t("job_progress");
  const deleteItemLabel = isOperationType ? t("delete_task") : t("delete_job");
  const workerLabel = isOperationType ? t("project") : t("job");
  // Evidence Gallery Modal
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  const [imageUrl, setImageUrl] = useState(null);
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  // Get organization timezone for time display
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();

  const toOrgTimeMoment = timeString => {
    if (!timeString) return null;
    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(timeString);
    const normalized = hasTimezone ? timeString : `${timeString}Z`;
    return moment(normalized).tz(orgTimezone);
  };
  // ToolTip
  const [selectIndex, setSelectIndex] = useState(-1);
  const [toolTip, setToolTip] = useState(-1);
  // Extract audio URLs for the custom hook (from both assignees and subtasks)
  const assigneeAudioUrls =
    jobDetails?.assignees?.reduce((urls, assignee) => {
      const evidences = assignee?.evidences || (assignee?.evidence ? [assignee.evidence] : []);
      evidences.forEach(evidence => {
        if (evidence?.audio) {
          urls.push(evidence.audio);
        }
      });
      return urls;
    }, []) || [];
  
  const subtaskAudioUrls =
    jobDetails?.subtasks?.reduce((urls, subtask) => {
      if (subtask?.evidences && Array.isArray(subtask.evidences)) {
        subtask.evidences.forEach(evidence => {
          if (evidence?.audio) {
            urls.push(evidence.audio);
          }
        });
      }
      return urls;
    }, []) || [];
  
  const audioUrls = [...assigneeAudioUrls, ...subtaskAudioUrls];
  // Use custom hook for audio durations
  const { getDuration } = useAudioDurations(audioUrls);

  /**
   * Collect evidences from a specific assignee (supports multiple evidences)
   * @param {Object} assignee - Assignee object
   * @returns {Array} Array of evidence items
   */
  const collectAssigneeEvidences = (assignee) => {
    const evidences = [];
    // Get evidences array (or convert single evidence to array for backward compatibility)
    const assigneeEvidences = assignee?.evidences || (assignee?.evidence ? [assignee.evidence] : []);
    
    assigneeEvidences.forEach((ev, index) => {
      if (ev?.image) {
        evidences.push({
          type: 'image',
          src: ev.image,
          label: `${assignee.first_name} ${assignee.last_name} - Image${assigneeEvidences.length > 1 ? ` ${index + 1}` : ''}`,
        });
      }
      if (ev?.audio) {
        evidences.push({
          type: 'audio',
          src: ev.audio,
          label: `${assignee.first_name} ${assignee.last_name} - Audio${assigneeEvidences.length > 1 ? ` ${index + 1}` : ''}`,
        });
      }
    });
    return evidences;
  };

  /**
   * Collect evidences from a specific subtask
   * @param {Object} subtask - Subtask object
   * @returns {Array} Array of evidence items
   */
  const collectSubtaskEvidences = (subtask) => {
    const evidences = [];
    if (subtask?.evidences && Array.isArray(subtask.evidences)) {
      subtask.evidences.forEach((evidence, index) => {
        if (evidence?.image) {
          evidences.push({
            type: 'image',
            src: evidence.image,
            label: `${subtask.name} - Image ${index + 1}`,
          });
        }
        if (evidence?.audio) {
          evidences.push({
            type: 'audio',
            src: evidence.audio,
            label: `${subtask.name} - Audio ${index + 1}`,
          });
        }
      });
    }
    return evidences;
  };

  /**
   * Open gallery with specific evidence items and start index
   * @param {Array} items - Array of evidence items
   * @param {number} startIndex - Index to start at
   */
  const openGallery = (items, startIndex = 0) => {
    setGalleryItems(items);
    setGalleryStartIndex(startIndex);
    setGalleryOpen(true);
  };

  /**
   * Handle evidence click from assignee section
   * @param {Object} assignee - Assignee object
   * @param {string} evidenceSrc - Source URL of clicked evidence
   */
  const handleAssigneeEvidenceClick = (assignee, evidenceSrc) => {
    const items = collectAssigneeEvidences(assignee);
    const index = items.findIndex(item => item.src === evidenceSrc);
    openGallery(items, index >= 0 ? index : 0);
  };

  /**
   * Handle evidence click from subtask section
   * @param {Object} subtask - Subtask object
   * @param {string} evidenceSrc - Source URL of clicked evidence
   */
  const handleSubtaskEvidenceClick = (subtask, evidenceSrc) => {
    const items = collectSubtaskEvidences(subtask);
    const index = items.findIndex(item => item.src === evidenceSrc);
    openGallery(items, index >= 0 ? index : 0);
  };

  const handleCloseGallery = () => {
    setGalleryOpen(false);
    setGalleryItems([]);
    setGalleryStartIndex(0);
  };

  /**
   * Wrapper for LineItemSection - handles evidence clicks from line items
   * @param {Object} subtask - Subtask object (will be received from LineItemSection)
   * @param {string} evidenceSrc - Evidence source URL
   */
  const handleLineItemImageClick = (evidenceSrc) => {
    // LineItemSection doesn't pass subtask object, so we need to find it
    const subtask = jobDetails?.subtasks?.find(st => 
      st.evidences?.some(ev => ev.image === evidenceSrc || ev.audio === evidenceSrc)
    );
    if (subtask) {
      handleSubtaskEvidenceClick(subtask, evidenceSrc);
    }
  };

  const toolTipChange = (value, index) => {
    setToolTip(value);
    setSelectIndex(value === -1 ? -1 : index);
  };

  // Helper function to parse and format time in organization timezone
  const formatTimeInOrgTimezone = timeString => {
    const timeMoment = toOrgTimeMoment(timeString);
    return timeMoment ? timeMoment.format("h:mm a") : "";
  };

  useEffect(() => {
    if (jobDetails && jobDetails.location && jobDetails.location.id) {
      const baseUrl = `https://maps.googleapis.com/maps/api/staticmap?key=${process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY}&size=600x300&zoom=15&scale=1&language=eng`;
      const markers = `&markers=color:red%7C${jobDetails.location.latitude},${jobDetails.location.longitude}`;
      const path = `&path=fillcolor:0x6f7aff33%7Cweight:2%7Ccolor:0x6f7affff${getMapCircle(
        jobDetails.location.latitude,
        jobDetails.location.longitude,
        jobDetails.location.radius,
      )}`;
      const url = `${baseUrl}${markers}${path}`;

      setImageUrl(url);
      setIsLocationLoading(false);
    }
  }, [jobDetails]);

  const shouldShowAnytime = () => {
    // If is_anytime is true, always show "Anytime" regardless of actual times
    if (jobDetails.is_anytime) {
      return true;
    }
    // If is_anytime is false, show actual times
    return false;
  };

  const editJobPage = () => {
    // This function can be implemented if navigation is needed
    // For now, it's a placeholder
  };

  return (
    <RightSlidingPanel
      isOpen={isOpen}
      closePanel={toggle}
      width="800px"
      overlayZIndex={2}
      contentZIndex={2}
    >
      <EvidenceGalleryModal
        isOpen={galleryOpen}
        items={galleryItems}
        startIndex={galleryStartIndex}
        onClose={handleCloseGallery}
      />
      {isLoading ? (
        <ComponentCircleLoader />
      ) : (
        <CommonGrid padding="30px" gap="20px" columns="1fr">
          <CommonFlex
            alignItems="start"
            flexFlow="column"
            justifyContent="start"
            width="100%"
          >
            {/* job id and status section */}
            <CommonFlex
              alignItems="start"
              justifyContent="space-between"
              flexFlow="row"
              gap="5px"
              width="100%"
            >
              <CommonText margin="0 0 10px 0" name title fontSize="14px">
                {jobLabel}: #
                {jobDetails?.job_code ||
                  `#${jobDetails?.id?.slice(0, 8) || ""}`}
              </CommonText>
              <CommonStatusContainer>
                <StatusDot status={jobDetails?.status?.toLowerCase()} />
                <CommonText name title fontSize="13px">
                  {jobDetails?.status
                    ? jobDetails.status.charAt(0).toUpperCase() +
                      jobDetails.status.slice(1).replace(/_/g, " ")
                    : "-"}
                </CommonText>
              </CommonStatusContainer>
            </CommonFlex>

            {/* job name section */}
            <CommonText margin="0 0 10px 0" name title fontSize="20px">
              {jobDetails?.name || "-"}
            </CommonText>

            {/* worker and status section */}
            {isOperationType && <CommonGrid columns="1fr" gap="10px" style={{ width: "100%" }}>
              <CommonFlex
                alignItems="start"
                flexFlow="column"
                gap="5px"
                style={{ marginBottom: "10px" }}
              >
                <CommonText fontSize="14px">{workerLabel}:</CommonText>
                <CommonText name fontSize="13px">
                  {jobDetails?.work_order?.name || "-"}
                </CommonText>
              </CommonFlex>
            </CommonGrid>}

            {/* progress and customer section */}
            <CommonGrid
              columns={orgType === "operation" ? "1fr" : "1fr 1fr"}
              gap="10px"
              style={{ width: "100%" }}
              alignItem="flex-start"
            >
              {orgType === "operation" ? (
                // For operation type: show job progress in place of customer
                <CommonFlex
                  alignItems="flex-start"
                  flexFlow="column"
                  gap="5px"
                  style={{ marginBottom: "10px" }}
                >
                  <CommonText fontSize="14px">{itemProgressLabel}:</CommonText>
                  <CircularProgress
                    percent={jobDetails?.percentage_complete || 0}
                    size={50}
                    strokeWidth={4}
                  />
                </CommonFlex>
              ) : (
                // For service type: show both customer and job progress
                <>
                  <CommonFlex
                    alignItems="start"
                    flexFlow="column"
                    gap="10px"
                    style={{ marginBottom: "10px" }}
                  >
                    <CommonText style={{ margin: "0px" }} fontSize="14px">
                      Customer:
                    </CommonText>
                    <CommImageNameComp
                      imgSource={
                        jobDetails?.work_order?.client_image || avatar5
                      }
                      name={jobDetails?.work_order?.client_name || "-"}
                      size="40px"
                      fontSize="13px"
                      index={2}
                      color={jobDetails?.work_order?.client_color}
                      email={jobDetails?.work_order?.client_email}
                    />
                  </CommonFlex>

                  <CommonFlex
                    alignItems="flex-start"
                    flexFlow="column"
                    gap="5px"
                    style={{ marginBottom: "10px" }}
                  >
                    <CommonText fontSize="14px">
                      {itemProgressLabel}:
                    </CommonText>
                    <CircularProgress
                      percent={jobDetails?.percentage_complete || 0}
                      size={50}
                      strokeWidth={4}
                    />
                  </CommonFlex>
                </>
              )}
            </CommonGrid>
            {/* description section */}
            {jobDetails?.description && (
              <CommonFlex alignItems="start" flexFlow="column" gap="5px">
                <CommonText fontSize="14px">Description: </CommonText>
                <CommonText name fontSize="13px">
                  {jobDetails.description}
                </CommonText>
              </CommonFlex>
            )}
          </CommonFlex>
          {/* location section */}
          <CommonGrid columns="1fr" gap="10px">
            <CommonText fontSize="14px">Location:</CommonText>
            {jobDetails && jobDetails.location != null ? (
              <CommonCardContainer>
                <CommonImage
                  style={{ borderRadius: "5px" }}
                  src={imageUrl}
                  alt="map"
                />
                <CommonText>
                  {jobDetails && jobDetails?.location?.address}
                </CommonText>
              </CommonCardContainer>
            ) : (
              <CommonText style={{ marginTop: "0px" }}>
                No location found
              </CommonText>
            )}
          </CommonGrid>

          <HorizontalLineDivider />

          {/* card section */}
          <CommonFlex
            width="100%"
            alignItems="flex-start"
            flexFlow="column"
            gap="10px"
          >
            <CommonText fontSize="14px">Schedule for</CommonText>
            {jobDetails && jobDetails.status === "unscheduled" && (
              <>
                {/* Unscheduled Schedule */}
                <ScheduleForContainer
                  style={{
                    marginRight: "10px",
                    width: "100%",
                    gridTemplateColumns: "1fr 150px",
                  }}
                >
                  <ScheduleForItemContainer>
                    <CommonText name title fontSize="16px">
                      {" "}
                      Unscheduled Job
                    </CommonText>
                  </ScheduleForItemContainer>
                  <ColoredButton
                    style={{ width: "max-content", borderRadius: "30px" }}
                    onClick={editJobPage}
                  >
                    Schedule Now
                  </ColoredButton>
                </ScheduleForContainer>
              </>
            )}

            {jobDetails &&
              jobDetails.schedule_type &&
              jobDetails.status !== "unscheduled" && (
                <>
                  {/* Daily Schedule */}
                  {jobDetails.schedule_type === "daily" && (
                    <ScheduleForContainer>
                      <ScheduleForItemContainer>
                        <CommonFlex gap="10px" alignItems="flex-start">
                          <CommonImage
                            src={calendarIcon}
                            alt="calendar"
                            width="20px"
                            height="20px"
                          />
                          <CommonText name title fontSize="16px">
                            {jobDetails.start_time
                              ? moment(jobDetails.start_time + "Z")
                                  .tz(orgTimezone)
                                  .format("D MMM YYYY")
                              : "-"}
                          </CommonText>
                        </CommonFlex>

                        {shouldShowAnytime() ? (
                          <CommonFlex gap="10px" alignItems="flex-start">
                            <CommonText name fontSize="14px">
                              Time:
                            </CommonText>
                            <CommonText name title fontSize="14px">
                              Anytime of this day
                            </CommonText>
                          </CommonFlex>
                        ) : (
                          jobDetails.start_time &&
                          jobDetails.end_time && (
                            <CommonFlex gap="10px" alignItems="flex-start">
                              <CommonText name fontSize="14px">
                                Start Time:
                              </CommonText>
                              <CommonText name title fontSize="14px">
                                {formatTimeInOrgTimezone(jobDetails.start_time)}
                              </CommonText>
                              <CommonText name fontSize="14px">
                                End Time:
                              </CommonText>
                              <CommonText name title fontSize="14px">
                                {formatTimeInOrgTimezone(jobDetails.end_time)}
                              </CommonText>
                            </CommonFlex>
                          )
                        )}

                        {jobDetails.end_time && (
                          <CommonFlex gap="10px" alignItems="flex-start">
                            <CommonText name fontSize="14px">
                              Ending Date:
                            </CommonText>
                            <CommonText name title fontSize="14px">
                              {moment(jobDetails.end_time + "Z")
                                .tz(orgTimezone)
                                .format("D MMM YYYY")}
                            </CommonText>
                          </CommonFlex>
                        )}
                      </ScheduleForItemContainer>
                      {jobDetails.recurring && (
                        <SemiRoundedButton>Daily</SemiRoundedButton>
                      )}
                    </ScheduleForContainer>
                  )}

                  {/* Weekly Schedule */}
                  {jobDetails.schedule_type === "weekly" && (
                    <ScheduleForContainer>
                      <ScheduleForItemContainer>
                        <CommonFlex gap="10px" alignItems="flex-start">
                          <CommonImage
                            src={calendarIcon}
                            alt="calendar"
                            width="20px"
                            height="20px"
                          />
                          {/* Show date similar to daily schedule */}
                          <CommonText name title fontSize="16px">
                            {jobDetails.start_time
                              ? moment(jobDetails.start_time + "Z")
                                  .tz(orgTimezone)
                                  .format("D MMM YYYY")
                              : jobDetails.end_time
                              ? moment(jobDetails.end_time + "Z")
                                  .tz(orgTimezone)
                                  .format("D MMM YYYY")
                              : "-"}
                          </CommonText>
                        </CommonFlex>

                        {shouldShowAnytime() ? (
                          <CommonFlex gap="10px" alignItems="flex-start">
                            <CommonText name fontSize="14px">
                              Time:
                            </CommonText>
                            <CommonText name title fontSize="14px">
                              Anytime of this day
                            </CommonText>
                          </CommonFlex>
                        ) : (
                          jobDetails.start_time &&
                          jobDetails.end_time && (
                            <CommonFlex gap="10px" alignItems="flex-start">
                              <CommonText name fontSize="14px">
                                Start Time:
                              </CommonText>
                              <CommonText name title fontSize="14px">
                                {formatTimeInOrgTimezone(jobDetails.start_time)}
                              </CommonText>
                              <CommonText name fontSize="14px">
                                End Time:
                              </CommonText>
                              <CommonText name title fontSize="14px">
                                {formatTimeInOrgTimezone(jobDetails.end_time)}
                              </CommonText>
                            </CommonFlex>
                          )
                        )}

                        {jobDetails.end_time && (
                          <CommonFlex gap="10px" alignItems="flex-start">
                            <CommonText name fontSize="14px">
                              Ending Date:
                            </CommonText>
                            <CommonText name title fontSize="14px">
                              {moment(jobDetails.end_time + "Z")
                                .tz(orgTimezone)
                                .format("D MMM YYYY")}
                            </CommonText>
                          </CommonFlex>
                        )}
                      </ScheduleForItemContainer>
                      <SemiRoundedButton>Weekly</SemiRoundedButton>
                    </ScheduleForContainer>
                  )}

                  {/* Monthly Schedule */}
                  {jobDetails.schedule_type === "monthly" && (
                    <ScheduleForContainer>
                      <ScheduleForItemContainer>
                        <CommonFlex gap="10px" alignItems="flex-start">
                          <CommonImage
                            src={calendarIcon}
                            alt="calendar"
                            width="20px"
                            height="20px"
                          />
                          {/* Show date similar to daily schedule */}
                          <CommonText name title fontSize="16px">
                            {jobDetails.start_time
                              ? moment(jobDetails.start_time + "Z")
                                  .tz(orgTimezone)
                                  .format("D MMM YYYY")
                              : jobDetails.end_time
                              ? moment(jobDetails.end_time + "Z")
                                  .tz(orgTimezone)
                                  .format("D MMM YYYY")
                              : "-"}
                          </CommonText>
                        </CommonFlex>

                        {shouldShowAnytime() ? (
                          <CommonFlex gap="10px" alignItems="flex-start">
                            <CommonText name fontSize="14px">
                              Time:
                            </CommonText>
                            <CommonText name title fontSize="14px">
                              Anytime of this day
                            </CommonText>
                          </CommonFlex>
                        ) : (
                          jobDetails.start_time &&
                          jobDetails.end_time && (
                            <CommonFlex gap="10px" alignItems="flex-start">
                              <CommonText name fontSize="14px">
                                Start Time:
                              </CommonText>
                              <CommonText name title fontSize="14px">
                                {formatTimeInOrgTimezone(jobDetails.start_time)}
                              </CommonText>
                              <CommonText name fontSize="14px">
                                End Time:
                              </CommonText>
                              <CommonText name title fontSize="14px">
                                {formatTimeInOrgTimezone(jobDetails.end_time)}
                              </CommonText>
                            </CommonFlex>
                          )
                        )}

                        {jobDetails.end_time && (
                          <CommonFlex gap="10px" alignItems="flex-start">
                            <CommonText name fontSize="14px">
                              Ending Date:
                            </CommonText>
                            <CommonText name title fontSize="14px">
                              {moment(jobDetails.end_time + "Z")
                                .tz(orgTimezone)
                                .format("D MMM YYYY")}
                            </CommonText>
                          </CommonFlex>
                        )}
                      </ScheduleForItemContainer>
                      <SemiRoundedButton>Monthly</SemiRoundedButton>
                    </ScheduleForContainer>
                  )}
                </>
              )}
          </CommonFlex>
          <HorizontalLineDivider />

          {/* Assignees section */}
          {jobDetails &&
            jobDetails.assignees &&
            jobDetails.assignees.length > 0 && (
              <CommonFlex flexFlow="column" alignItems="flex-start" gap="0px">
                <CommonText margin="0 0 10px 0" fontSize="14px">
                  Assignee list
                </CommonText>
                {jobDetails &&
                  jobDetails.assignees &&
                  jobDetails.assignees.length > 0 && (
                    <>
                      <AssigneeListTableHeaderContainer
                        justifyContent="flex-start"
                        columns="200px 100px 100px 100px auto"
                      >
                        <TableItem>
                          <TableText>Assignee</TableText>
                        </TableItem>
                        <TableItem>
                          <TableText>Check In</TableText>
                        </TableItem>
                        <TableItem>
                          <TableText>Check Out</TableText>
                        </TableItem>
                        <TableItem>
                          <TableText>Status</TableText>
                        </TableItem>
                        <TableItem>
                          <TableText>Evidence</TableText>
                        </TableItem>
                      </AssigneeListTableHeaderContainer>
                      {jobDetails?.assignees?.map((assignee, index) => {
                        if (role === "member" && user_id !== assignee.id) {
                          return null;
                        }
                        return (
                          <AssigneeListTableRowContainer
                            justifyContent="flex-start"
                            key={assignee.id || index}
                            columns="
                            200px
                            100px
                            100px
                            100px
                            auto
                          "
                          >
                            {/* Assignee name */}
                            <TableItem>
                              <div key={assignee.id}>
                                <MemberNameDiv style={{ cursor: "pointer" }}>
                                  <CommImageNameComp
                                    imgSource={assignee.avatar || avatar5}
                                    name={`${assignee.first_name} ${assignee.last_name}`}
                                    size="36px"
                                    fontSize="13px"
                                    index={index}
                                    color={assignee.color}
                                    hoverUnderline
                                    designation={assignee.designation}
                                    showAddDesignation={false}
                                  />
                                </MemberNameDiv>
                              </div>
                            </TableItem>
                            {/* Clock In */}
                            <TableItem>
                              <CommonText>
                                {assignee.checkedin &&
                                assignee.checkedin.timestamp
                                  ? moment
                                      .unix(assignee.checkedin.timestamp)
                                      .format("h:mm a")
                                  : "-"}
                                {assignee.checkedin &&
                                  assignee.checkedin.timestamp &&
                                  (jobDetails.is_anytime === "true" ||
                                  jobDetails.is_anytime === true ? (
                                    <GreenCircle>
                                      <img src={tick} alt="" width="100%" />
                                    </GreenCircle>
                                  ) : moment
                                      .unix(assignee.checkedin.timestamp)
                                      .isAfter(
                                        moment(jobDetails.start_time + "Z").tz(
                                          orgTimezone,
                                        ),
                                      ) ? (
                                    <LateTag>{t("late")}</LateTag>
                                  ) : (
                                    <GreenCircle>
                                      <img src={tick} alt="" width="100%" />
                                    </GreenCircle>
                                  ))}
                              </CommonText>
                            </TableItem>
                            {/* Clock Out */}
                            <TableItem>
                              <CommonText>
                                {assignee.checkedout &&
                                assignee.checkedout.timestamp
                                  ? moment
                                      .unix(assignee.checkedout.timestamp)
                                      .format("h:mm a")
                                  : "-"}
                                {assignee.checkedout &&
                                  assignee.checkedout.timestamp &&
                                  (jobDetails.is_anytime === "true" ||
                                  jobDetails.is_anytime === true ? (
                                    <GreenCircle>
                                      <img src={tick} alt="" width="100%" />
                                    </GreenCircle>
                                  ) : moment
                                      .unix(assignee.checkedout.timestamp)
                                      .isAfter(
                                        moment(jobDetails.end_time + "Z").tz(
                                          orgTimezone,
                                        ),
                                      ) ? (
                                    <LateTag>{t("late")}</LateTag>
                                  ) : (
                                    <GreenCircle>
                                      <img src={tick} alt="" width="100%" />
                                    </GreenCircle>
                                  ))}
                              </CommonText>
                            </TableItem>
                            {/* Status */}
                            <TableItem>
                              <Status status={assignee.status} />
                            </TableItem>
                            {/* Evidence */}
                            <TableItem>
                              <EvidenceIconSection gap="15px">
                                {(() => {
                                  const allEvidences = assignee?.evidences || (assignee?.evidence ? [assignee.evidence] : []);
                                  const visibleEvidences = allEvidences.slice(0, 4);
                                  const hasMore = allEvidences.length > 4;
                                  
                                  return (
                                    <>
                                      {visibleEvidences.map((ev, evIndex) => (
                                        <Fragment key={`assignee-evidence-${index}-${evIndex}`}>
                                          {ev?.image && (
                                            <EvidenceIcon
                                              onClick={() =>
                                                handleAssigneeEvidenceClick(assignee, ev.image)
                                              }
                                            >
                                              <CommonImage
                                                src={ev.image}
                                                alt=""
                                                style={{
                                                  width: "100%",
                                                  height: "100%",
                                                }}
                                              />
                                            </EvidenceIcon>
                                          )}
                                          {ev?.audio && (
                                            <EvidenceIcon
                                              onClick={() =>
                                                handleAssigneeEvidenceClick(assignee, ev.audio)
                                              }
                                            >
                                              <CommonImage
                                                src={audioIcon}
                                                alt=""
                                                size="20px"
                                              />
                                              <span
                                                style={{
                                                  fontSize: "10px",
                                                  color: "#2266EF",
                                                  fontWeight: "600",
                                                }}
                                              >
                                                {getDuration(ev.audio)}
                                              </span>
                                            </EvidenceIcon>
                                          )}
                                        </Fragment>
                                      ))}
                                      {hasMore && (
                                        <EvidenceIcon
                                          onClick={() => {
                                            const fifthEvidence = allEvidences[4];
                                            const evidenceSrc = fifthEvidence?.image || fifthEvidence?.audio;
                                            if (evidenceSrc) {
                                              handleAssigneeEvidenceClick(assignee, evidenceSrc);
                                            }
                                          }}
                                          style={{
                                            backgroundColor: '#2266EF',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                          }}
                                        >
                                          +{allEvidences.length - 4}
                                        </EvidenceIcon>
                                      )}
                                      {allEvidences.length === 0 && (
                                        <CommonText fontSize="12px">-</CommonText>
                                      )}
                                    </>
                                  );
                                })()}
                              </EvidenceIconSection>
                            </TableItem>
                          </AssigneeListTableRowContainer>
                        );
                      })}
                    </>
                  )}
              </CommonFlex>
            )}

          {jobDetails &&
            jobDetails.subtasks && isOperationType && 
            jobDetails.subtasks.length > 0 && (
              <>
                <HorizontalLineDivider />
                <SubtaskTableContainer>
                  <CommonText title name margin="0 0 20px 0" fontSize="14px">
                    Subtasks:
                  </CommonText>
                  <SubTaskTableHeaderContainer
                    style={{ width: "100%", borderRadius: "5px 5px 0 0" }}
                    columns="100px 120px 130px 100px max-content 100px"
                  >
                    <TableItem>
                      <TableText>Name</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>Description</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>Evidence</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>Status</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>Note</TableText>
                    </TableItem>
                  </SubTaskTableHeaderContainer>
                  {jobDetails.subtasks.map((subtask, index) => (
                    <SubTaskTableRowContainer
                      key={subtask.id || index}
                      style={{ width: "100%" }}
                      columns="100px 120px 130px 100px max-content 100px"
                    >
                      <TableItem>{subtask.name || "-"}</TableItem>
                      <TableItem>{subtask.description || "-"}</TableItem>
                      <TableItem>
                        <EvidenceIconSection>
                          {subtask.evidences &&
                            subtask.evidences.length > 0 &&
                            subtask.evidences.map((evidence, evIndex) => (
                              <Fragment key={`subtask-${subtask.id || index}-evidence-${evidence.id || evIndex}`}>
                                {evidence.image && (
                                  <EvidenceIcon
                                    onClick={() => handleSubtaskEvidenceClick(subtask, evidence.image)}
                                    // onMouseEnter={() =>
                                    //   toolTipChange(1, `${index}-${evIndex}`)
                                    // }
                                    // onMouseLeave={() => toolTipChange(-1)}
                                  >
                                    <CommonImage
                                      src={evidence.image}
                                      alt=""
                                      style={{ width: "100%", height: "100%" }}
                                    />
                                    
                                  </EvidenceIcon>
                                )}
                                {evidence.audio && (
                                  <EvidenceIcon
                                    onClick={() =>
                                      handleSubtaskEvidenceClick(subtask, evidence.audio)
                                    }
                                    // onMouseEnter={() =>
                                    //   toolTipChange(2, `${index}-${evIndex}`)
                                    // }
                                    // onMouseLeave={() => toolTipChange(-1)}
                                  >
                                    <CommonImage
                                      src={audioIcon}
                                      alt=""
                                      size="20px"
                                    />
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        color: "#2266EF",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {getDuration(evidence.audio)}
                                    </span>
                                    {/* {selectIndex === `${index}-${evIndex}` &&
                                      toolTip === 2 && (
                                        <ToolTipContainer
                                          style={{
                                            position: "relative",
                                            top: "-57px",
                                          }}
                                        >
                                          <InnerDivTooltip>
                                            <img
                                              src={Chat}
                                              alt="chat"
                                              width="64px"
                                            />
                                            <TooltipText>Audio</TooltipText>
                                          </InnerDivTooltip>
                                        </ToolTipContainer>
                                      )} */}
                                  </EvidenceIcon>
                                )}
                              </Fragment>
                            ))}
                          {(!subtask.evidences ||
                            subtask.evidences.length === 0) && (
                            <CommonText fontSize="12px">-</CommonText>
                          )}
                        </EvidenceIconSection>
                      </TableItem>
                      <TableItem>
                        <CommonFlex
                          flexFlow="column"
                          gap="3px"
                          alignItems="flex-start"
                        >
                          {subtask.status && <Status status={subtask.status} />}
                          {subtask.percentage_complete && (
                            <CommonText
                              fontSize="11px"
                              style={{ color: "#666" }}
                            >
                              {parseFloat(subtask.percentage_complete).toFixed(
                                0,
                              )}
                              % complete
                            </CommonText>
                          )}
                          {!subtask.status && !subtask.percentage_complete && (
                            <CommonText fontSize="12px">-</CommonText>
                          )}
                        </CommonFlex>
                      </TableItem>
                      <TableItem>
                          <CommonText>{subtask.evidences.note || "-"}</CommonText>
                        </TableItem>
                    </SubTaskTableRowContainer>
                  ))}
                </SubtaskTableContainer>
              </>
            )}
          <HorizontalLineDivider />

          {/* Line Items section */}
          {jobDetails &&
              orgType !== "operation" &&
              jobDetails.subtasks &&
              jobDetails.subtasks.length > 0 && (
                <>
                  {/* <HorizontalLineDivider /> */}
                  <LineItemSection
                    pageType="detail"
                    subtasks={jobDetails?.subtasks}
                    onImageClick={handleLineItemImageClick}
                    onAudioClick={handleLineItemImageClick}
                    getDuration={getDuration}
                    pageName="approval"
                  />
                </>
              )}

{jobDetails &&
              orgType !== "operation" &&
              jobDetails?.job_documents &&
              jobDetails?.job_documents.length > 0 && (
                <>
                  <HorizontalLineDivider />
                  <CommonCardContainer bgColor="#EDF8FE">
                  <DocumentUploadSection
                      pageType="detail"
                      documents={jobDetails?.job_documents}
                    />
                  </CommonCardContainer>
                </>
              )}
     

          {/* Supervisor Note */}
          {jobDetails &&
            (jobDetails.employee_note || jobDetails.supervisor_note) && (
              <NoteSection>
                {jobDetails && jobDetails.employee_note && (
                  <>
                    <CommonText fontSize="16px">
                      {t("employee_note")}
                    </CommonText>
                    <CommonText>{jobDetails.employee_note}</CommonText>
                  </>
                )}
                <HorizontalLineDivider />
                {jobDetails && jobDetails.supervisor_note && (
                  <>
                    <CommonText fontSize="16px">
                      {t("supervisor_note")}
                    </CommonText>
                    <CommonText>{jobDetails.supervisor_note}</CommonText>
                  </>
                )}
              </NoteSection>
            )}

          {jobDetails &&
            jobDetails.approval &&
            jobDetails.approval.status === "pending" &&
            (role === "owner" || role === "admin") && (
              <CommonGrid
                gap="32px"
                justifyContent="space-between"
                columns="1.5fr .5fr"
              >
                <CommonFlex justifyContent="flex-start" gap="10px">
                  <CommonText $title color="black">
                    Waiting for Approval
                  </CommonText>
                  <CommonFlex gap="10px">
                    <ApproveRejectButton
                      type="reject"
                      onClick={() => {
                        if (setRejectedJobInfo) {
                          setRejectedJobInfo({
                            id: jobDetails.id,
                            approval: jobDetails.approval,
                            type: "rejected",
                          });
                        }
                        setSupervisorNoteModalOpen(true);
                      }}
                    >
                      Reject
                    </ApproveRejectButton>
                    <ApproveRejectButton
                      type="approve"
                      onClick={() => {
                        if (updateTaskStatusRequest) {
                          updateTaskStatusRequest(
                            jobDetails.id,
                            jobDetails.approval,
                            "approved",
                            "",
                          );
                        }
                      }}
                    >
                      Approve
                    </ApproveRejectButton>
                  </CommonFlex>
                </CommonFlex>
                <div></div>
              </CommonGrid>
            )}
        </CommonGrid>
      )}
    </RightSlidingPanel>
  );
};

export default ApprovalRightpanel;
